import type { AuthzChallenge } from "@gram/client/models/components/authzchallenge.js";
import type { ChallengeBucket } from "@gram/client/models/components/challengebucket.js";

type Reason = AuthzChallenge["reason"];

/** Keep only buckets that have a scope and a fully-specified resource. */
export function isDisplayableBucket(b: ChallengeBucket): boolean {
  return !!b.scope && !!b.resourceKind && !!b.resourceId;
}

export function getInitials(identifier: string): string {
  const name = identifier.split("@")[0] ?? identifier;
  return name.slice(0, 2).toUpperCase();
}

export function canAssignChallengeRole(
  challenge: Pick<ChallengeBucket, "principalType" | "principalUrn">,
): boolean {
  return (
    challenge.principalType === "user" && challenge.principalUrn !== "user:all"
  );
}

export function principalDisplayName(
  userEmail: string | undefined,
  principalUrn: string,
): string {
  if (userEmail) return userEmail;
  if (principalUrn === "user:all") return "All users";
  return principalUrn;
}

export function reasonLabel(reason: Reason): string {
  switch (reason) {
    case "grant_matched":
      return "Access granted — a matching role permission was found.";
    case "no_grants":
      return "No permissions configured for this identity.";
    case "scope_unsatisfied":
      return "The identity's roles don't include this permission.";
    case "deny_grant":
      return "Access denied — a deny grant explicitly blocks this permission.";
    case "invalid_check":
      return "The authorization check was malformed or invalid.";
    case "rbac_skipped_apikey":
      return "API keys bypass role checks — access was allowed directly.";
    case "dev_override":
      return "Access allowed via a development override.";
  }
  return "Authorization status unavailable.";
}

/**
 * Filter challenges by principal and scope. When a filter is "all", it is
 * not applied — all challenges pass through.
 */
export function scopeChallenges(
  challenges: AuthzChallenge[],
  principalFilter: string,
  scopeFilter: string,
): AuthzChallenge[] {
  let base = challenges;
  if (principalFilter !== "all") {
    base = base.filter(
      (c) => (c.userEmail ?? c.principalUrn) === principalFilter,
    );
  }
  if (scopeFilter !== "all") {
    base = base.filter((c) => c.scope === scopeFilter);
  }
  return base;
}

export type ChallengeCounts = {
  all: number;
  deny: number;
  allow: number;
  resolved: number;
};

/**
 * Compute pill counts from a list of challenges.
 * Resolved challenges are counted separately regardless of outcome.
 */
export function countChallenges(challenges: AuthzChallenge[]): ChallengeCounts {
  const c: ChallengeCounts = {
    all: challenges.length,
    deny: 0,
    allow: 0,
    resolved: 0,
  };
  for (const ch of challenges) {
    if (ch.resolvedAt) {
      c.resolved++;
    } else if (ch.outcome === "deny") {
      c.deny++;
    } else {
      c.allow++;
    }
  }
  return c;
}
